import {
  Box,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Badge,
  Center,
  Loader,
  Alert,
} from "@mantine/core";
import {
  IconCoins,
  IconWallet,
  IconAlertTriangle,
  IconCircleCheck,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { usePerformRequest } from "../../../hooks/usePerformRequest";

type Float = {
  owedToMembers: number;
  owedMemberCount: number;
  membersOwingUs: number;
  owingMemberCount: number;
  monnifyAvailable: number;
  monnifyLedger: number;
  coverageOk: boolean;
  coverageGap: number;
  totalPaidOut: number;
  pendingWithdrawals: number;
  pendingWithdrawalCount: number;
  totalClawedBack: number;
  generatedAt: string;
};

const naira = (n: number) => `₦${(n || 0).toLocaleString()}`;

function Stat({
  label,
  value,
  sub,
  color,
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb={4}>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          {label}
        </Text>
        {icon}
      </Group>
      <Text size="1.6rem" fw={800} c={color}>
        {value}
      </Text>
      {sub && (
        <Text size="xs" c="dimmed" mt={4}>
          {sub}
        </Text>
      )}
    </Card>
  );
}

export default function Finance() {
  const { data, isLoading } = usePerformRequest<{ data: Float }>({
    method: Endpoints.GetFloatSummary().method,
    route: Endpoints.GetFloatSummary().route,
  });
  const f = data?.data;

  return (
    <Box p="md">
      <Group mb="xs" gap="xs">
        <IconCoins size={24} />
        <Title order={3}>Float &amp; Liability</Title>
      </Group>
      <Text c="dimmed" size="sm" mb="lg">
        What we owe members vs what we actually have to pay them, plus payouts and
        fraud recovery.
      </Text>

      {isLoading || !f ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          {/* Coverage banner — the headline: can we cover what we owe? */}
          {f.coverageOk ? (
            <Alert
              color="teal"
              variant="light"
              icon={<IconCircleCheck size={18} />}
              mb="lg"
            >
              Covered — Monnify balance ({naira(f.monnifyAvailable)}) covers what we
              owe members ({naira(f.owedToMembers)}).
            </Alert>
          ) : (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              mb="lg"
            >
              <b>Shortfall of {naira(f.coverageGap)}</b> — we owe members{" "}
              {naira(f.owedToMembers)} but Monnify only has{" "}
              {naira(f.monnifyAvailable)}. Top up the wallet.
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <Stat
              label="Owed to members"
              value={naira(f.owedToMembers)}
              sub={`${f.owedMemberCount} members with a balance`}
              color="orange"
              icon={<IconCoins size={18} />}
            />
            <Stat
              label="Monnify available"
              value={naira(f.monnifyAvailable)}
              sub={`ledger ${naira(f.monnifyLedger)}`}
              color={f.coverageOk ? "teal" : "red"}
              icon={<IconWallet size={18} />}
            />
            <Stat
              label="Pending withdrawals"
              value={naira(f.pendingWithdrawals)}
              sub={`${f.pendingWithdrawalCount} in flight`}
            />
            <Stat
              label="Members owing us"
              value={naira(f.membersOwingUs)}
              sub={`${f.owingMemberCount} negative balances (clawbacks)`}
              color={f.membersOwingUs > 0 ? "grape" : undefined}
            />
            <Stat
              label="Total paid out"
              value={naira(f.totalPaidOut)}
              sub="completed withdrawals, all time"
            />
            <Stat
              label="Fraud recovered"
              value={naira(f.totalClawedBack)}
              sub="reversals + manual clawbacks"
              color="teal"
            />
          </SimpleGrid>

          <Group mt="lg" justify="flex-end">
            <Badge variant="light" color="gray">
              as of {new Date(f.generatedAt).toLocaleString()}
            </Badge>
          </Group>
        </>
      )}
    </Box>
  );
}
